import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import {
  MOCK_CANDIDATES,
  compositeScore,
} from "@/components/hub/mockSelectionData";

// ---------------------------------------------------------------------------
// Static mock stats — enough for a demo (keyed by candidate id)
// ---------------------------------------------------------------------------
const MOCK_STATS: Record<number, { battingAvg: number; bowlingAvg: number; mvpRank: number | null; status: "Selected" | "Reserve" | "Not Selected" }> = {
  1:  { battingAvg: 42.3, bowlingAvg: 18.2, mvpRank: 2,    status: "Selected"     },
  2:  { battingAvg: 28.1, bowlingAvg: 12.4, mvpRank: 1,    status: "Selected"     },
  3:  { battingAvg: 35.7, bowlingAvg: 22.0, mvpRank: 4,    status: "Reserve"      },
  4:  { battingAvg: 19.4, bowlingAvg: 10.8, mvpRank: 3,    status: "Selected"     },
  5:  { battingAvg: 38.9, bowlingAvg: 25.6, mvpRank: 5,    status: "Reserve"      },
  6:  { battingAvg: 22.0, bowlingAvg: 14.1, mvpRank: null, status: "Not Selected" },
  7:  { battingAvg: 45.5, bowlingAvg: 30.0, mvpRank: null, status: "Not Selected" },
  8:  { battingAvg: 17.3, bowlingAvg: 9.7,  mvpRank: null, status: "Not Selected" },
  9:  { battingAvg: 31.0, bowlingAvg: 20.3, mvpRank: 1,    status: "Selected"     },
  10: { battingAvg: 24.8, bowlingAvg: 15.9, mvpRank: 2,    status: "Reserve"      },
  11: { battingAvg: 40.2, bowlingAvg: 28.4, mvpRank: 1,    status: "Selected"     },
  12: { battingAvg: 26.6, bowlingAvg: 11.3, mvpRank: 2,    status: "Reserve"      },
  13: { battingAvg: 33.4, bowlingAvg: 16.7, mvpRank: 1,    status: "Selected"     },
  14: { battingAvg: 29.8, bowlingAvg: 21.5, mvpRank: 2,    status: "Reserve"      },
};

const AGE_GROUPS = ["All", "U11", "U13", "U15", "U17"] as const;
type AgeFilter = (typeof AGE_GROUPS)[number];

const STATUS_BADGE: Record<string, string> = {
  "Selected":     "bg-green-100 text-green-800 border-green-300",
  "Reserve":      "bg-blue-100 text-blue-800 border-blue-300",
  "Not Selected": "bg-gray-100 text-gray-600 border-gray-300",
};

export default function HubPerformanceStatsDemo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramAge = searchParams.get("ageGroup") as AgeFilter | null;
  const [ageFilter, setAgeFilter] = useState<AgeFilter>(
    AGE_GROUPS.includes(paramAge as AgeFilter) ? (paramAge as AgeFilter) : "All"
  );

  function selectAge(ag: AgeFilter) {
    setAgeFilter(ag);
    if (ag === "All") {
      setSearchParams({});
    } else {
      setSearchParams({ ageGroup: ag });
    }
  }

  const rows = useMemo(() => {
    const filtered = ageFilter === "All"
      ? MOCK_CANDIDATES
      : MOCK_CANDIDATES.filter((c) => c.ageGroup === ageFilter);
    return filtered
      .map((c) => ({ candidate: c, composite: compositeScore(c.id), stats: MOCK_STATS[c.id] }))
      .sort((a, b) => b.composite - a.composite);
  }, [ageFilter]);

  return (
    <HubAdminDemoLayout>
      <div className="px-4 md:px-8 py-6 space-y-5" data-testid="hub-performance-stats-page">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Performance Stats</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Per-player batting, bowling, MVP rank, and composite selection score.
          </p>
        </div>

        {/* Age group filter pills */}
        <div className="flex flex-wrap gap-2" data-testid="age-filter-pills">
          {AGE_GROUPS.map((ag) => (
            <button
              key={ag}
              onClick={() => selectAge(ag)}
              data-testid={`age-pill-${ag.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                ageFilter === ag
                  ? "bg-[#1E2761] text-white border-[#1E2761]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
              }`}
            >
              {ag}
            </button>
          ))}
        </div>

        {/* Stats table */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm" data-testid="stats-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3 text-left">Player</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Position</th>
                <th className="px-4 py-3 text-center">Composite</th>
                <th className="px-4 py-3 text-center">Bat Avg</th>
                <th className="px-4 py-3 text-center">Bowl Avg</th>
                <th className="px-4 py-3 text-center">MVP Rank</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(({ candidate, composite, stats }, idx) => (
                <tr
                  key={candidate.id}
                  className="hover:bg-gray-50 transition-colors"
                  data-testid={`stats-row-${candidate.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-4 shrink-0">{idx + 1}</span>
                      <div>
                        <p className="font-semibold text-gray-900">{candidate.name}</p>
                        <p className="text-xs text-gray-400">{candidate.ageGroup}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{candidate.position}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-bold text-gray-800 bg-gray-100 rounded-full px-2 py-0.5 text-xs">
                      {composite.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700">{stats?.battingAvg.toFixed(1) ?? "—"}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{stats?.bowlingAvg.toFixed(1) ?? "—"}</td>
                  <td className="px-4 py-3 text-center">
                    {stats?.mvpRank != null ? (
                      <span className="font-bold text-amber-600">#{stats.mvpRank}</span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge className={`border text-xs ${STATUS_BADGE[stats?.status ?? "Not Selected"]}`}>
                      {stats?.status ?? "Not Selected"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-gray-400 text-center">
          DEMO — Sample data · Sorted by composite score (descending)
        </p>
      </div>
    </HubAdminDemoLayout>
  );
}
